export interface IToast {
  id: string;
  message: string;
  type: 'success' | 'danger' | 'warning' | 'info';
  duration?: number;
}

class ToastService {
  private toasts: IToast[] = [];
  private listeners: Array<(toasts: IToast[]) => void> = [];
  private defaultDuration = 3000;
  private timeouts: Record<string, NodeJS.Timeout> = {};

  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener([...this.toasts]));
  }

  private addToast(toast: IToast): void {
    try {
      this.toasts.push(toast);
      this.notifyListeners();

      this.timeouts[toast.id] = setTimeout(() => {
        this.removeToast(toast.id);
      }, toast.duration || this.defaultDuration);
    } catch (error) {
      console.error('Error displaying toast message:', error);
    }
  }

  removeToast(id: string): void {
    if (this.timeouts[id]) {
      clearTimeout(this.timeouts[id]);
      delete this.timeouts[id];
    }

    this.toasts = this.toasts.filter((toast) => toast.id !== id);
    this.notifyListeners();
  }

  showSuccess(message: string, duration = 3000): void {
    const toast: IToast = {
      id: this.generateId(),
      message,
      type: 'success',
      duration,
    };
    this.addToast(toast);
  }

  showError(message: string, duration = 5000): void {
    const toast: IToast = {
      id: this.generateId(),
      message,
      type: 'danger',
      duration,
    };
    this.addToast(toast);
  }

  showWarning(message: string, duration?: number): void {
    const toast: IToast = {
      id: this.generateId(),
      message,
      type: 'warning',
      duration,
    };
    this.addToast(toast);
  }

  showInfo(message: string, duration?: number): void {
    const toast: IToast = {
      id: this.generateId(),
      message,
      type: 'info',
      duration,
    };
    this.addToast(toast);
  }

  show(
    message: string,
    type: 'success' | 'danger' | 'warning' | 'info' = 'success',
    duration?: number,
  ): void {
    const toast: IToast = {
      id: this.generateId(),
      message,
      type,
      duration,
    };
    this.addToast(toast);
  }

  clearAll(): void {
    Object.values(this.timeouts).forEach((timeout) => clearTimeout(timeout));
    this.timeouts = {};
    this.toasts = [];
    this.notifyListeners();
  }

  subscribe(listener: (toasts: IToast[]) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  getToasts(): IToast[] {
    return [...this.toasts];
  }
}

export const toastService = new ToastService();

